"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RpcWsTransportForClient = void 0;
const rpc_core_1 = require("@wranggle/rpc-core");
const constants_iso_1 = require("@wranggle/constants-iso");
const shared_utils_iso_1 = require("@wranggle/shared-utils-iso");
const extract_and_parse_message_1 = require("./extract-and-parse-message");
const reconnecting_websocket_1 = require("./reconnecting-websocket");
const DefaultConnectionTimeoutMs = constants_iso_1.MinuteMs * 3; // todo: should correspond to TTL of auth token
/**
 * This uses the reconnecting-websocket.ts to set up initial connection. After that, it stops reconnection attempts, requiring
 *   instead an onDisconnect notification mechanism to start from scratch. Because the RpcEndpoint needs to be built from
 *   scratch currently.
 */
class RpcWsTransportForClient {
    constructor(args) {
        this._endpointListenerByChannel = {};
        this._isStopped = false;
        this._disconnectHandlers = [];
        this._hasConnected = false;
        const { url, onDisconnect } = args;
        this._logger = args.logger || shared_utils_iso_1.loggerIsoLib();
        if (!onDisconnect) {
            this._logger.warn({ url }, 'RpcWsTransportForClient onDisconnect is required');
            throw new Error('InvalidRpcWsTransportForClientArgs');
        }
        this._url = url;
        this.addDisconnectHandler(onDisconnect);
        this._promisedConnection = shared_utils_iso_1.createPromisedResolution();
        this._setupConnectionTimeout(args.maxConnectionTimeoutMs || DefaultConnectionTimeoutMs);
        this._setupConnection(args.extraConnectionArgs);
        this._setupListeners();
    }
    _setupConnection(extraConnectionArgs) {
        this._connection = reconnecting_websocket_1._createWebsocketConnection(Object.assign(Object.assign({ backoffOpts: {
                backoffInitialDelay: 750,
                backoffMaxDelayMs: 1000 * 90,
            }, pingMs: 1000 * 22, isReconnectDesired: () => __awaiter(this, void 0, void 0, function* () {
                return !this._hasConnected;
            }) }, extraConnectionArgs), { url: this._url }));
    }
    _setupListeners() {
        const { addWebSocketListener, disconnect, getCurrentWebSocket } = this._connection;
        const logger = this._logger;
        const url = this._url;
        addWebSocketListener('open', () => {
            logger.info('WebSocket upgrade/connect succeeded. Will continue with RPC setup');
            this._hasConnected = true;
            this._ws = getCurrentWebSocket();
            this._promisedConnection.resolve(true);
        });
        addWebSocketListener('error', (evt) => {
            const err = evt.message || (evt.target || {}).message || (evt.target || {}).data;
            logger.info({
                errorMessage: err,
                url,
            }, 'RpcWsTransportForClient encountered WebSocket error');
            if (this._hasConnected) {
                if (this._connection) {
                    this._connection.disconnect();
                }
                this._handleCloseOrError(err);
            }
        });
        addWebSocketListener('close', () => {
            this._handleCloseOrError();
        });
    }
    promisedConnection() {
        return this._promisedConnection;
    }
    listen(msgHandler, channel) {
        if (!this._listenHandler) {
            this._listenHandler = this._processInboundMessage.bind(this);
            this._ws.on('message', this._listenHandler);
        }
        this._endpointListenerByChannel[channel] = msgHandler;
    }
    sendMessage(payload) {
        if (!this._ws) {
            throw new Error('Sending message before ready. WsRpcClient does not buffer messages.');
        }
        this._ws.send(shared_utils_iso_1.safeStringify(payload));
    }
    stopTransport() {
        this._isStopped = true;
        this._connection && this._connection.disconnect();
        this._cleanup();
    }
    getRawWebSocket() {
        return this._ws;
    }
    addDisconnectHandler(fn) {
        this._disconnectHandlers.push(fn);
    }
    _processInboundMessage(rawMsg) {
        if (this._isStopped) {
            return;
        }
        const handlersByChannel = this._endpointListenerByChannel;
        let payload;
        try {
            payload = extract_and_parse_message_1.extractAndParseMessage(rawMsg);
        }
        catch (err) { // todo: when refactoring this into a base transport class in rpc-core, find lib that checks if msg is json using first n chars
            this._debug(rpc_core_1.LogActivity.TransportReceivingMessage, { ignoringNonJsonMessage: rawMsg });
        }
        if (payload) {
            const targetChannel = payload.channel;
            const handler = handlersByChannel[targetChannel];
            this._debug(rpc_core_1.LogActivity.TransportReceivingMessage, { payload, handler: !!handler });
            if (handler) {
                try {
                    handler(payload);
                }
                catch (error) {
                    this._debug(rpc_core_1.LogActivity.TransportReceivingMessage, { reason: 'Error handling rpc message, possibly in WranggleRpc core', error });
                }
            }
        }
    }
    _debug(activity, data) {
        if (!this.debugHandler) {
            return;
        }
        this.debugHandler(Object.assign({
            activity,
            endpointSenderId: this.endpointSenderId,
        }, data));
    }
    _cleanup() {
        // am assuming disconnect/close will also clean up listener references. Else: this._wsClient.removeListener('message', this._listenHandler);
        this._isStopped = true;
        this._listenHandler = undefined;
        this._promisedConnection = undefined;
        this._ws = undefined;
        this._endpointListenerByChannel = {};
        this._connection = undefined;
    }
    _handleCloseOrError(errMessage) {
        if (!this._hasConnected) {
            return;
        }
        const logger = this._logger;
        this._isStopped = true;
        const error = {
            errorCode: 'WebSocketTransportFailed',
            problem: errMessage,
        };
        logger.info({ url: this._url }, 'WebSocket transport for RPC was closed or encountered an error. Will trigger onDisconnect.');
        while (this._disconnectHandlers.length) {
            const cb = this._disconnectHandlers.shift();
            try {
                cb(error);
            }
            catch (err) {
                this._logger.warn({ error: shared_utils_iso_1.jsErrorToRecord(err) }, 'Error in onDisconnect callback');
            }
        }
        this._cleanup();
    }
    _setupConnectionTimeout(timeoutMs) {
        this._connectionTimeout = setTimeout(() => {
            if (!this._hasConnected && this._promisedConnection && this._promisedConnection.isPending()) {
                this._logger.info({
                    url: this._url
                }, 'Failed to establish a WebSocket connection to RPC server.');
                this._promisedConnection.resolve({
                    errorCode: 'WsConnectionErrorOrClosed',
                });
            }
        }, timeoutMs);
    }
}
exports.RpcWsTransportForClient = RpcWsTransportForClient;
//# sourceMappingURL=rpc-ws-transport-for-client.js.map